import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useScreenSize, ScreenSizeEnum } from "shared/use-screen-size";
import { ProfileFormData, NotificationOptions } from "./profile-components/profile-form-data";
import { useForm } from "react-hook-form";
import { ProfileEmail } from "./profile-components/profile-email";
import { ProfileName } from "./profile-components/profile-name";
import { useValidateProfileInputs } from "./hooks/use-profile-validation";
import { useGetApiProfileinfoQuery } from "api/generatedApi";
import { CircularProgress } from "@mui/material";
import { ProfileCompany } from "./profile-components/profile-Company";
import { ProfileEmailNotification } from "./profile-components/profile-email-notification";
import { useProfileSubmit } from "./hooks/use-profile-submit";
import { getPCA } from "index";

export const AccountProfile = () => {
  const { t } = useTranslation();
  const { data: profile } = useGetApiProfileinfoQuery();
  const { onSubmit } = useProfileSubmit();
  const { screenSize } = useScreenSize();
  const {
    getValues,
    register,
    setError,
    watch,
    control,
    formState: { errors },
    reset,
  } = useForm<ProfileFormData>({
    mode: "all",
    defaultValues: profile
      ? {
          firstName: profile.firstName ?? "",
          lastName: profile.lastName ?? "",
          email: profile.email ?? "",
          companyName: profile.company ?? "",
          notificationOptions: profile.notificationOptions ?? [],
        }
      : {},
  });

  const msalInstance = getPCA();
  const account = msalInstance.getActiveAccount();
  const showLoginEmail = account?.idTokenClaims?.idp?.includes("tekniq") ?? false;

  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    if (profile) {
      reset({
        firstName: profile.firstName ?? "",
        lastName: profile.lastName ?? "",
        email: profile.email ?? "",
        companyName: profile.company ?? "",
        notificationOptions: profile.notificationOptions ?? [],
      });
    }
  }, [profile, reset]);

  //validation functions for the inputs
  const { validateHasEmail, validateHasValidEmail, validateHasFirstName, validateHasLastName, validateEditParameter } = useValidateProfileInputs(getValues);
  const { email } = watch();
  const onAdd = () => {
    if (!validateHasFirstName()) {
      setError("firstName", { type: "validate", message: t("profilePage.firstNameRequired") });
    }
    if (!validateHasLastName()) {
      setError("lastName", { type: "validate", message: t("profilePage.lastNameRequired") });
    }
    if (!validateHasEmail()) {
      setError("email", { type: "custom", message: t("profilePage.emailRequired") });
    } else if (!validateHasValidEmail()) {
      setError("email", { type: "custom", message: t("profilePage.emailInvalid") });
    }

    if (!validateEditParameter()) {
      return;
    }
    onSubmit(getValues());
    setEditMode(false);
  };

  const handleRegret = () => {
    reset();
    setEditMode(false);
  };

  //check to see if profil is available, if not show loading spinner
  if (profile) {
    return (
      <Box
        sx={{
          maxWidth: screenSize === ScreenSizeEnum.Mobile ? "100vw" : "fit-content",
          minHeight: screenSize === ScreenSizeEnum.Mobile ? "100vh" : undefined,
          p: screenSize === ScreenSizeEnum.Mobile ? 1 : 3,
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
        }}
      >
        <Typography variant="h5" color="primary.main" sx={{ marginBottom: 2 }}>
          {t("account.profilePage.title")}
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, color: "primary.main", width: screenSize === ScreenSizeEnum.Mobile ? "100%" : undefined }}>
          <ProfileName register={register} editMode={editMode} firstName={profile.firstName ?? ""} lastName={profile.lastName ?? ""} firstNameError={errors.firstName} lastNameError={errors.lastName} />
          <ProfileEmail register={register} editMode={editMode} notificationEmail={profile.email} loginEmail={account?.username} showLoginMail={showLoginEmail} fieldError={errors.email} />
          <ProfileCompany register={register} editMode={editMode} companyName={profile.company} fieldError={errors.companyName} />
          <ProfileEmailNotification notificationOptions={(profile.notificationOptions as NotificationOptions[]) ?? []} editMode={editMode} control={control} />
        </Box>

        <>
          {editMode ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Button variant="outlined" color="primary" onClick={handleRegret}>
                {t("common.regret")}
              </Button>
              <Button variant="contained" color="primary" onClick={onAdd}>
                {t("common.save")}
              </Button>
            </Box>
          ) : (
            <Button variant="contained" color="primary" onClick={() => setEditMode(true)}>
              {t("common.edit")}
            </Button>
          )}
        </>
        {/*this box makes sure the content is not cut off by the bottom menu */}
        <Box sx={{ p: "60px" }} />
      </Box>
    );
  }
  //_______________________________________________________________________________________________________________________________________
  //if profile is not available, show loading spinner
  //_______________________________________________________________________________________________________________________________________
  else {
    return (
      <Box sx={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <CircularProgress size={150} />
      </Box>
    );
  }
};
